import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { SitesPage, SiteDetailPage } from './pages/SitesPage';
import { SitesManagementPage } from './pages/SitesManagementPage';
import { SiteOnboardingPage } from './pages/SiteOnboardingPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientsManagementPage } from './pages/ClientsManagementPage';
import { ClientBillingPage } from './pages/ClientBillingPage';
import { BillingPage } from './pages/BillingPage';
import { MeteringPage } from './pages/MeteringPage';
import { DataIngestionPage } from './pages/DataIngestionPage';
import { DataQualityPage } from './pages/DataQualityPage';
import { AlarmsPage } from './pages/AlarmsPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { UsersPage } from './pages/UsersPage';
import AssetDetailPage from './pages/AssetDetailPage';
import ProspectsListPage from './pages/ProspectsListPage';
import ProspectDetailPage from './pages/ProspectDetailPage';
import ExternalIntakePage from './pages/ExternalIntakePage';
import VerificationQueuePage from './pages/VerificationQueuePage';
import PricingMasterPage from './pages/PricingMasterPage';
import ProposalBuilderV2Page, { ProposalEditorV2Page } from './pages/ProposalBuilderV2Page';
import ProspectSiteDetailPage from './pages/ProspectSiteDetailPage';
import RentalSandboxPage from './pages/RentalSandboxPage';
import ConfiguratorSandboxPage from './pages/ConfiguratorSandboxPage';
import RFQDetailPage from './pages/RFQDetailPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ReportsPage from './pages/ReportsPage';
import DieselPricingPage from './pages/DieselPricingPage';
import JEMIntegrationPage from './pages/JEMIntegrationPage';
import { WhatsAppIntegrationPage } from './pages/WhatsAppIntegrationPage';
import SalesAgentPage from './pages/SalesAgentPage';
import SalesInboxPage from './pages/SalesInboxPage';
import ConfigSummaryPage from './pages/ConfigSummaryPage';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/sites"
              element={
                <ProtectedRoute>
                  <SitesManagementPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/sites/:siteId"
              element={
                <ProtectedRoute>
                  <SiteDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/sites/:siteId/onboarding"
              element={
                <ProtectedRoute >
                  <SiteOnboardingPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/assets/:assetId"
              element={
                <ProtectedRoute>
                  <AssetDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/clients"
              element={
                <ProtectedRoute >
                  <ClientsManagementPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/clients/:clientId/billing"
              element={
                <ProtectedRoute >
                  <ClientBillingPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/clients/:clientId"
              element={
                <ProtectedRoute >
                  <ClientDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/reports"
              element={
                <ProtectedRoute >
                  <ReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/diesel-prices"
              element={
                <ProtectedRoute >
                  <DieselPricingPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/integrations/jem"
              element={
                <ProtectedRoute >
                  <JEMIntegrationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/integrations/whatsapp"
              element={
                <ProtectedRoute>
                  <WhatsAppIntegrationPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/billing"
              element={
                <ProtectedRoute >
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/metering"
              element={
                <ProtectedRoute >
                  <MeteringPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/prospects"
              element={
                <ProtectedRoute >
                  <ProspectsListPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/prospects/sites/:siteId"
              element={
                <ProtectedRoute >
                  <ProspectSiteDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/prospects/:prospectId"
              element={
                <ProtectedRoute >
                  <ProspectDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/verification-queue"
              element={
                <ProtectedRoute >
                  <VerificationQueuePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/pricing"
              element={
                <ProtectedRoute >
                  <PricingMasterPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/proposals"
              element={
                <ProtectedRoute >
                  <ProposalBuilderV2Page />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/proposals/:proposalId"
              element={
                <ProtectedRoute >
                  <ProposalEditorV2Page />
                </ProtectedRoute>
              }
            />
            
            {/* Public intake page - no auth */}
            <Route path="/intake/:token" element={<ExternalIntakePage />} />
            
            <Route
              path="/rfq/:rfqId"
              element={
                <ProtectedRoute >
                  <RFQDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/rental-sandbox"
              element={
                <ProtectedRoute >
                  <RentalSandboxPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/configurator-sandbox"
              element={
                <ProtectedRoute >
                  <ConfiguratorSandboxPage />
                </ProtectedRoute>
              }
            />
            
            {/* Internal catalog preview (behind auth) */}
            <Route
              path="/catalog/preview/products"
              element={
                <ProtectedRoute >
                  <ProductsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog/preview/products/:slug"
              element={
                <ProtectedRoute >
                  <ProductDetailPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/data-ingestion"
              element={
                <ProtectedRoute >
                  <DataIngestionPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/data-quality"
              element={
                <ProtectedRoute >
                  <DataQualityPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/alarms"
              element={
                <ProtectedRoute>
                  <AlarmsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute >
                  <AuditLogsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/users"
              element={
                <ProtectedRoute >
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/sales-agent"
              element={
                <ProtectedRoute>
                  <SalesAgentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-inbox"
              element={
                <ProtectedRoute>
                  <SalesInboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/config-summary"
              element={
                <ProtectedRoute >
                  <ConfigSummaryPage />
                </ProtectedRoute>
              }
            />
            
            {/* Sidebar IA aliases — keep canonical routes above intact.
                These mirror the new sidebar URLs onto the existing pages so
                deep links and bookmarks keep working. */}
            <Route
              path="/sales/inbox"
              element={
                <ProtectedRoute>
                  <SalesInboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/prospects"
              element={
                <ProtectedRoute>
                  <ProspectsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/verify-queue"
              element={
                <ProtectedRoute>
                  <VerificationQueuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/proposals"
              element={
                <ProtectedRoute>
                  <ProposalBuilderV2Page />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/pricing-catalog"
              element={
                <ProtectedRoute>
                  <PricingMasterPage />
                </ProtectedRoute>
              }
            />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
